import React from 'react';
import { motion } from 'framer-motion';
import { KeyIcon, CloudArrowUpIcon, DocumentTextIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import CodeBlock from '../components/CodeBlock';
import SEO from '../components/SEO';

function ApiSection({ icon: Icon, title, description, code, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="w-full"
    >
      <div className="glass p-8 rounded-modern backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300">
        <div className="flex items-center mb-6">
          <div className="p-3 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-modern mr-4">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent">{title}</h2>
        </div>
        <p className="text-white/80 mb-6 leading-relaxed">{description}</p>
        <CodeBlock code={code} language="bash" />
      </div>
    </motion.div>
  );
}

function ApiDocs() {
  const apiSections = [
    {
      icon: KeyIcon,
      title: "Authentication",
      description: "All API requests require authentication using your API key in the Authorization header. You can get your API key from your dashboard after subscribing to a plan.",
      code: `# Using cURL
curl -H "Authorization: Bearer YOUR_API_KEY" \\
  https://api.remuvr.com/v1/auth

# Response
{
  "status": "success",
  "message": "API key is valid",
  "plan": "pro",
  "requestsRemaining": 4985,
  "resetDate": "2024-02-29T00:00:00Z"
}`
    },
    {
      icon: CloudArrowUpIcon,
      title: "Remove Background",
      description: "Upload an image to remove its background. The API supports cross-origin requests from any domain with a valid API key. Maximum file size: 10MB.",
      code: `# Using cURL
curl -X POST \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "file=@image.jpg" \\
  https://remuvr-api.onrender.com/remove

# JavaScript Example
async function removeBackground(imageFile) {
  const formData = new FormData();
  formData.append('file', imageFile);

  const response = await fetch(
    'https://remuvr-api.onrender.com/remove',
    {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY'
      },
      body: formData
    }
  );

  if (!response.ok) {
    throw new Error('Failed to process image');
  }

  const processedImage = await response.blob();
  return URL.createObjectURL(processedImage);
}

# Python Example
import requests

def remove_background(image_path):
    url = 'https://remuvr-api.onrender.com/remove'
    headers = {
        'Authorization': 'Bearer YOUR_API_KEY'
    }
    
    with open(image_path, 'rb') as image:
        files = {'file': image}
        response = requests.post(url, headers=headers, files=files)
    
    if response.status_code == 200:
        with open('result.png', 'wb') as f:
            f.write(response.content)
        return 'result.png'
    else:
        raise Exception(f'Error: {response.text}')`
    },
    {
      icon: DocumentTextIcon,
      title: "Check Job Status",
      description: "Check the status of your background removal request using the job ID. Processing time varies by plan, with Pro and Business plans getting priority processing.",
      code: `# Using cURL
curl -H "Authorization: Bearer YOUR_API_KEY" \\
  https://api.remuvr.com/v1/status/550e8400-e29b-41d4-a716-446655440000

# Response (Processing)
{
  "status": "success",
  "job": {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "status": "processing",
    "createdAt": "2024-01-29T04:53:19+08:00"
  }
}

# Response (Completed)
{
  "status": "success",
  "job": {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "status": "completed",
    "createdAt": "2024-01-29T04:53:19+08:00",
    "completedAt": "2024-01-29T04:53:21+08:00",
    "result": "https://cdn.remuvr.com/processed/550e8400.png"
  }
}`
    },
    {
      icon: ChartBarIcon,
      title: "Usage Statistics",
      description: "Get your current API usage statistics and limits. Pro plan includes 5,000 API calls/month, Business plan includes 25,000 API calls/month, and Enterprise plans have custom limits.",
      code: `# Using cURL
curl -H "Authorization: Bearer YOUR_API_KEY" \\
  https://api.remuvr.com/v1/usage

# Response
{
  "status": "success",
  "usage": {
    "plan": "business",
    "totalRequests": 150,
    "remainingRequests": 24850,
    "resetDate": "2024-02-29T00:00:00Z",
    "limits": {
      "maxFileSize": 50000000,
      "requestsPerSecond": 20
    }
  }
}`
    }
  ];

  return (
    <div className="min-h-screen bg-[#0F172A] relative overflow-hidden">
      <SEO 
        title="API Documentation | Remuvr Background Removal API"
        description="Integrate Remuvr's powerful background removal API into your applications. Comprehensive documentation, code examples, and API reference. RESTful API with 99.9% uptime and enterprise-grade support."
        url="https://remuvr.com/api-docs"
        pageName="api-docs"
        type="website"
      />
      <div className="absolute inset-0 bg-[url('/src/assets/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))] opacity-20"></div>
      
      <div className="container mx-auto px-4 py-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent">
            API Documentation
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            Integrate our powerful background removal API into your applications.
            Available with Pro, Business, and Enterprise plans.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 mb-16 max-w-4xl mx-auto">
          {apiSections.map((section, index) => (
            <ApiSection key={index} {...section} index={index} />
          ))}
        </div>

        {/* Rate Limits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass p-8 rounded-modern max-w-4xl mx-auto mb-16 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300"
        >
          <h2 className="text-2xl font-bold mb-6 bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent">Rate Limits</h2>
          <div className="space-y-4">
            <p className="text-white/80 leading-relaxed">
              Rate limits vary by plan:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 leading-relaxed">
              <li>Pro: 5,000 API calls per month, max 10 requests per second</li>
              <li>Business: 25,000 API calls per month, max 20 requests per second</li>
              <li>Enterprise: Custom limits based on your needs</li>
            </ul>
            <p className="text-white/80 leading-relaxed">
              When you exceed your rate limit, the API will return a 429 status code.
              Monthly limits reset on the first day of each month.
            </p>
          </div>
        </motion.div>

        {/* Error Handling */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass p-8 rounded-modern max-w-4xl mx-auto backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300"
        >
          <h2 className="text-2xl font-bold mb-6 bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent">Error Handling</h2>
          <div className="space-y-4">
            <p className="text-white/80 leading-relaxed">
              The API uses standard HTTP response codes and returns detailed error messages. All endpoints support cross-origin requests (CORS) from any domain.
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 leading-relaxed">
              <li>200: Success - Request completed successfully</li>
              <li>400: Bad Request - Invalid parameters or file format</li>
              <li>401: Unauthorized - Invalid or missing API key</li>
              <li>413: Payload Too Large - File size exceeds limit</li>
              <li>429: Too Many Requests - Rate limit exceeded</li>
              <li>500: Internal Server Error - Processing failed</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ApiDocs;